import { z } from 'zod';
import { COMMENT_BODY_REQUIRED, CommentVisibilitySchema } from './comments';
import { CronExpressionSchema } from './cron';
import { AutomationRunStatusSchema, EventTypeSchema, MAX_EVENT_DEPTH } from './events';
import { FieldKeySchema } from './fields/registry';
import { NOTIFICATION_CHANNELS, NotificationChannelSchema } from './notifications';
import { FieldRefSchema, FilterNodeSchema } from './query/ast';
import { TimeZoneSchema } from './tenancy';
import {
  BULK_TRANSITION_EXACTLY_ONE,
  DESCRIPTION_MAX,
  SUMMARY_MAX,
  TicketFieldsSchema,
  TransitionCommentSchema,
} from './tickets';
import { ConditionRuleSchema } from './workflow/rules';

/*
 * Automation rules (Unit 07; S-07-01 … S-07-12): trigger → conditions → actions as closed
 * JSON (ADR-0013 rules, ADR-0019). Conditions reuse the workflow grammar; `run` statuses live
 * in `events.ts` next to the `automation.executed` payload.
 */

/* ----------------------------- limits ----------------------------- */

/** Chain depth at which rules stop firing (ADR-0019 §2); same value as the event envelope cap. */
export const MAX_AUTOMATION_DEPTH = MAX_EVENT_DEPTH;
export const AUTOMATION_NAME_MAX = 120;
export const AUTOMATION_DESCRIPTION_MAX = 1000;
export const AUTOMATION_MAX_CONDITIONS = 20;
export const AUTOMATION_MAX_ACTIONS = 20;
export const AUTOMATION_COMMENT_MAX = 20_000;
export const NOTIFY_TEMPLATE_MAX = 2000;
export const NOTIFY_MAX_RECIPIENTS = 20;
export const NOTIFY_MAX_REPEATS = 10;
/** Longest delay / repeat interval: one week. */
export const NOTIFY_MAX_MINUTES = 7 * 24 * 60;
export const DATE_FIELD_OFFSET_MAX_DAYS = 365;

/* ----------------------------- triggers ----------------------------- */

export const CUSTOM_EVENT_TYPE_PATTERN = /^custom\.[a-z0-9_.]+$/;
export const CustomEventTypeSchema = z
  .string()
  .max(80)
  .regex(CUSTOM_EVENT_TYPE_PATTERN, 'custom event types look like custom.some_name');

/** Rules never fire on their own bookkeeping events (loop protection, events.md). */
export const TRIGGER_EXCLUDED_EVENT_TYPES = ['automation.executed', 'automation.due'] as const;

/** A registry type (minus the automation family) or a `custom.*` type from `emit_event`. */
export const TriggerEventTypeSchema = z.union([
  EventTypeSchema.exclude(TRIGGER_EXCLUDED_EVENT_TYPES),
  CustomEventTypeSchema,
]);
export type TriggerEventType = z.infer<typeof TriggerEventTypeSchema>;

/** System date columns a `date_field` trigger may watch; `cf_*` keys must be date / datetime. */
export const DATE_SYSTEM_FIELDS = [
  'created_at',
  'updated_at',
  'resolved_at',
  'due_at',
  'acknowledged_at',
] as const;
export const DateFieldRefSchema = z.union([z.enum(DATE_SYSTEM_FIELDS), FieldKeySchema]);
export type DateFieldRef = z.infer<typeof DateFieldRefSchema>;

export const AUTOMATION_TRIGGER_TYPES = [
  'event',
  'schedule',
  'sla.breached',
  'date_field',
] as const;

export const AutomationTriggerSchema = z.discriminatedUnion('type', [
  /** A matching domain event; `filter` narrows on the event's ticket (query AST). */
  z.strictObject({
    type: z.literal('event'),
    eventType: TriggerEventTypeSchema,
    filter: FilterNodeSchema.optional(),
  }),
  /** Calendar cadence in the given zone; the timers worker materialises the next occurrence. */
  z.strictObject({
    type: z.literal('schedule'),
    cron: CronExpressionSchema,
    timeZone: TimeZoneSchema,
  }),
  /** `sla.breached` for one policy or for any policy of the rule's scope. */
  z.strictObject({
    type: z.literal('sla.breached'),
    policyId: z.uuid().optional(),
  }),
  /** `field + offsetDays` per ticket; negative = before the date (S-07-07). */
  z.strictObject({
    type: z.literal('date_field'),
    field: DateFieldRefSchema,
    offsetDays: z.int().min(-DATE_FIELD_OFFSET_MAX_DAYS).max(DATE_FIELD_OFFSET_MAX_DAYS),
  }),
]);
export type AutomationTrigger = z.infer<typeof AutomationTriggerSchema>;
export type AutomationTriggerInput = z.input<typeof AutomationTriggerSchema>;

/* ----------------------------- actions ----------------------------- */

export const ASSIGN_STRATEGIES = ['fixed', 'round_robin', 'least_loaded'] as const;
export type AssignStrategy = (typeof ASSIGN_STRATEGIES)[number];

/** Pool strategies pick from the members of exactly one role or group. */
const PoolAssignSchema = z
  .strictObject({
    strategy: z.enum(['round_robin', 'least_loaded']),
    roleId: z.uuid().optional(),
    groupId: z.uuid().optional(),
  })
  .superRefine((v, ctx) => {
    if ((v.roleId === undefined ? 0 : 1) + (v.groupId === undefined ? 0 : 1) !== 1) {
      ctx.addIssue({ code: 'custom', message: 'provide exactly one of roleId or groupId' });
    }
  });

export const AssignTargetSchema = z.union([
  z.literal('unassigned'),
  z.strictObject({ strategy: z.literal('fixed'), userId: z.uuid() }),
  PoolAssignSchema,
]);
export type AssignTarget = z.infer<typeof AssignTargetSchema>;

export const NOTIFY_RECIPIENT_KINDS = [
  'reporter',
  'assignee',
  'watchers',
  'role',
  'user',
  'group',
] as const;

export const NotifyRecipientSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('reporter') }),
  z.strictObject({ kind: z.literal('assignee') }),
  z.strictObject({ kind: z.literal('watchers') }),
  z.strictObject({ kind: z.literal('role'), roleId: z.uuid() }),
  z.strictObject({ kind: z.literal('user'), userId: z.uuid() }),
  z.strictObject({ kind: z.literal('group'), groupId: z.uuid() }),
]);
export type NotifyRecipient = z.infer<typeof NotifyRecipientSchema>;

/** Recipient kinds resolved against the ticket; unavailable to `schedule` rules. */
export const TICKET_SCOPED_RECIPIENT_KINDS = ['reporter', 'assignee', 'watchers'] as const;

export const AUTOMATION_ACTION_TYPES = [
  'set_field',
  'assign',
  'transition',
  'add_comment',
  'notify',
  'emit_event',
  'create_ticket',
  'webhook',
] as const;
export type AutomationActionType = (typeof AUTOMATION_ACTION_TYPES)[number];

/** Actions that need the triggering ticket; unavailable to `schedule` rules. */
export const TICKET_SCOPED_ACTION_TYPES = [
  'set_field',
  'assign',
  'transition',
  'add_comment',
] as const;

export const AutomationActionSchema = z.discriminatedUnion('type', [
  /** Value validated by the field's type schema at run time; `$now` allowed for dates. */
  z.strictObject({
    type: z.literal('set_field'),
    field: FieldRefSchema,
    value: z.unknown(),
  }),
  z.strictObject({
    type: z.literal('assign'),
    to: AssignTargetSchema,
  }),
  /** Runs the workflow rules as `actor.type = 'automation'` (S-07-03). */
  z
    .strictObject({
      type: z.literal('transition'),
      transitionId: z.uuid().optional(),
      transitionKey: z.string().min(1).max(40).optional(),
      fields: TicketFieldsSchema.optional(),
      comment: TransitionCommentSchema.optional(),
    })
    .superRefine((a, ctx) => {
      const given = [a.transitionId, a.transitionKey].filter((v) => v !== undefined).length;
      if (given !== 1) {
        ctx.addIssue({
          code: 'custom',
          message: `${BULK_TRANSITION_EXACTLY_ONE}: provide exactly one of transitionId or transitionKey`,
        });
      }
    }),
  /** Body with `{{placeholders}}`, or a canned response (S-07-03). */
  z
    .strictObject({
      type: z.literal('add_comment'),
      body: z.string().max(AUTOMATION_COMMENT_MAX).optional(),
      visibility: CommentVisibilitySchema.default('internal'),
      cannedResponseId: z.uuid().optional(),
    })
    .superRefine((a, ctx) => {
      if (a.cannedResponseId === undefined && (a.body ?? '').trim().length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: `${COMMENT_BODY_REQUIRED}: body is required unless cannedResponseId is given`,
          path: ['body'],
        });
      }
    }),
  /**
   * Hands off to the notifications worker (S-08-06). `delayMinutes` arms a timer; with
   * `repeatEveryMinutes` the send repeats until `ticket.acknowledged` (when
   * `repeatUntilAcknowledged`) or `maxRepeats`.
   */
  z
    .strictObject({
      type: z.literal('notify'),
      channels: z.array(NotificationChannelSchema).min(1).max(NOTIFICATION_CHANNELS.length),
      recipients: z.array(NotifyRecipientSchema).min(1).max(NOTIFY_MAX_RECIPIENTS),
      /** Message text with `{{ticket.key}}`-style placeholders; channel default when absent. */
      template: z.string().max(NOTIFY_TEMPLATE_MAX).optional(),
      delayMinutes: z.int().min(0).max(NOTIFY_MAX_MINUTES).optional(),
      repeatEveryMinutes: z.int().min(1).max(NOTIFY_MAX_MINUTES).optional(),
      repeatUntilAcknowledged: z.boolean().optional(),
      maxRepeats: z.int().min(1).max(NOTIFY_MAX_REPEATS).default(NOTIFY_MAX_REPEATS),
    })
    .superRefine((a, ctx) => {
      if (new Set(a.channels).size !== a.channels.length) {
        ctx.addIssue({ code: 'custom', message: 'duplicate channel', path: ['channels'] });
      }
      if (a.repeatUntilAcknowledged && a.repeatEveryMinutes === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'repeatUntilAcknowledged needs repeatEveryMinutes',
          path: ['repeatEveryMinutes'],
        });
      }
    }),
  /** `custom.*` only; consumed by other rules (depth + 1) and webhooks. */
  z.strictObject({
    type: z.literal('emit_event'),
    eventType: CustomEventTypeSchema,
    data: z.record(z.string(), z.unknown()).optional(),
  }),
  /** Recurring tickets from `schedule` rules; text fields accept placeholders. */
  z.strictObject({
    type: z.literal('create_ticket'),
    projectId: z.uuid(),
    issueTypeId: z.uuid(),
    summary: z.string().trim().min(1).max(SUMMARY_MAX),
    description: z.string().max(DESCRIPTION_MAX).optional(),
    priorityId: z.uuid().optional(),
    assigneeId: z.uuid().optional(),
    fields: TicketFieldsSchema.optional(),
  }),
  /** Posts the triggering event's envelope to one subscription, regardless of its filters. */
  z.strictObject({
    type: z.literal('webhook'),
    subscriptionId: z.uuid(),
  }),
]);
export type AutomationAction = z.infer<typeof AutomationActionSchema>;
export type AutomationActionInput = z.input<typeof AutomationActionSchema>;

/* ----------------------------- rule-level validation ----------------------------- */

export interface AutomationRuleIssue {
  path: string;
  code: 'action_needs_ticket' | 'recipient_needs_ticket';
  message: string;
}

/**
 * Cross-property checks the schemas cannot express: `schedule` rules run without a ticket,
 * so ticket-scoped actions and dynamic recipients are invalid there. Pure; shared by the API
 * (create, and update after merging with the stored rule) and the rule builder.
 */
export function automationRuleIssues(rule: {
  trigger: AutomationTrigger;
  actions: readonly AutomationAction[];
}): AutomationRuleIssue[] {
  const issues: AutomationRuleIssue[] = [];
  if (rule.trigger.type !== 'schedule') {
    return issues;
  }
  rule.actions.forEach((action, i) => {
    if ((TICKET_SCOPED_ACTION_TYPES as readonly string[]).includes(action.type)) {
      issues.push({
        path: `actions.${i}`,
        code: 'action_needs_ticket',
        message: `${action.type} needs a ticket; schedule triggers run without one`,
      });
    }
    if (action.type === 'notify') {
      action.recipients.forEach((r, j) => {
        if ((TICKET_SCOPED_RECIPIENT_KINDS as readonly string[]).includes(r.kind)) {
          issues.push({
            path: `actions.${i}.recipients.${j}`,
            code: 'recipient_needs_ticket',
            message: `${r.kind} recipients need a ticket; schedule triggers run without one`,
          });
        }
      });
    }
  });
  return issues;
}

/* ----------------------------- rules ----------------------------- */

const ruleName = z.string().trim().min(1).max(AUTOMATION_NAME_MAX);
const ruleDescription = z.string().max(AUTOMATION_DESCRIPTION_MAX).nullable();
const ruleConditions = z.array(ConditionRuleSchema).max(AUTOMATION_MAX_CONDITIONS);
const ruleActions = z.array(AutomationActionSchema).min(1).max(AUTOMATION_MAX_ACTIONS);

/** `automation_rule` read model; `projectId` null = tenant-wide, `isSystem` = seeded default. */
export const AutomationRuleSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  enabled: z.boolean(),
  isSystem: z.boolean(),
  trigger: AutomationTriggerSchema,
  conditions: z.array(ConditionRuleSchema),
  actions: z.array(AutomationActionSchema),
  position: z.int().min(0),
  createdBy: z.uuid().nullable(),
  lastRunAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type AutomationRule = z.infer<typeof AutomationRuleSchema>;

const addRuleIssues = (
  rule: { trigger: AutomationTrigger; actions: readonly AutomationAction[] },
  ctx: z.RefinementCtx,
): void => {
  for (const issue of automationRuleIssues(rule)) {
    ctx.addIssue({ code: 'custom', message: issue.message, path: issue.path.split('.') });
  }
};

/** `POST /admin/v1/automations` (S-07-01). Conditions are "all of" (`[]` = always). */
export const CreateAutomationRuleSchema = z
  .strictObject({
    projectId: z.uuid().nullable().default(null),
    name: ruleName,
    description: ruleDescription.default(null),
    enabled: z.boolean().default(true),
    trigger: AutomationTriggerSchema,
    conditions: ruleConditions.default([]),
    actions: ruleActions,
    position: z.int().min(0).default(0),
  })
  .superRefine(addRuleIssues);
export type CreateAutomationRule = z.infer<typeof CreateAutomationRuleSchema>;
export type CreateAutomationRuleInput = z.input<typeof CreateAutomationRuleSchema>;

/**
 * `PATCH /admin/v1/automations/{id}`. A rule keeps its project; `isSystem` rules accept
 * `enabled` only (the API enforces that). Run `automationRuleIssues` on the merged rule.
 */
export const UpdateAutomationRuleSchema = z
  .strictObject({
    name: ruleName,
    description: ruleDescription,
    enabled: z.boolean(),
    trigger: AutomationTriggerSchema,
    conditions: ruleConditions,
    actions: ruleActions,
    position: z.int().min(0),
  })
  .partial();
export type UpdateAutomationRule = z.infer<typeof UpdateAutomationRuleSchema>;

/** `GET /admin/v1/automations?projectId=`; without `projectId` every rule of the tenant. */
export const AutomationRuleListQuerySchema = z.object({
  projectId: z.uuid().optional(),
  enabled: z.stringbool().optional(),
});
export type AutomationRuleListQuery = z.infer<typeof AutomationRuleListQuerySchema>;

export const AutomationRuleListResponseSchema = z.object({
  items: z.array(AutomationRuleSchema),
  nextCursor: z.string().nullable(),
});
export type AutomationRuleListResponse = z.infer<typeof AutomationRuleListResponseSchema>;

/* ----------------------------- runs ----------------------------- */

export const AutomationProblemSchema = z.object({
  path: z.string(),
  code: z.string(),
  message: z.string(),
});
export type AutomationProblem = z.infer<typeof AutomationProblemSchema>;

/** One action the run applied; `detail` carries ids (assignee chosen, comment id, …). */
export const AppliedActionSchema = z.object({
  index: z.int().min(0),
  type: z.enum(AUTOMATION_ACTION_TYPES),
  detail: z.record(z.string(), z.unknown()).optional(),
});
export type AppliedAction = z.infer<typeof AppliedActionSchema>;

/** `automation_run.result`; open for engine additions. */
export const AutomationRunResultSchema = z.looseObject({
  actionsApplied: z.array(AppliedActionSchema).default([]),
  problems: z.array(AutomationProblemSchema).default([]),
});
export type AutomationRunResult = z.infer<typeof AutomationRunResultSchema>;

/** Item of `GET /admin/v1/automations/{id}/runs`, newest first. */
export const AutomationRunSchema = z.object({
  id: z.uuid(),
  ruleId: z.uuid(),
  /** The triggering outbox event; null for dry runs. */
  eventId: z.uuid().nullable(),
  ticketId: z.uuid().nullable(),
  status: AutomationRunStatusSchema,
  depth: z.int().min(0),
  result: AutomationRunResultSchema,
  durationMs: z.int().min(0).nullable(),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
});
export type AutomationRun = z.infer<typeof AutomationRunSchema>;

export const AutomationRunListQuerySchema = z.object({
  status: AutomationRunStatusSchema.optional(),
  ticketId: z.uuid().optional(),
});
export type AutomationRunListQuery = z.infer<typeof AutomationRunListQuerySchema>;

export const AutomationRunListResponseSchema = z.object({
  items: z.array(AutomationRunSchema),
  nextCursor: z.string().nullable(),
});
export type AutomationRunListResponse = z.infer<typeof AutomationRunListResponseSchema>;

/* ----------------------------- dry run ----------------------------- */

/** `POST /admin/v1/automations/{id}/test` (S-07-12): evaluate against a ticket, apply nothing. */
export const DryRunRequestSchema = z.strictObject({
  ticketId: z.uuid(),
});
export type DryRunRequest = z.infer<typeof DryRunRequestSchema>;

export const DryRunResultSchema = z.object({
  triggerMatched: z.boolean(),
  conditions: z.array(z.object({ rule: ConditionRuleSchema, result: z.boolean() })),
  plannedActions: z.array(AutomationActionSchema),
  problems: z.array(AutomationProblemSchema),
});
export type DryRunResult = z.infer<typeof DryRunResultSchema>;
